import CreateProjectDialog from "@/components/projects/CreateProjectDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { format } from "date-fns";
import { FolderOpen, Loader2, Plus, Share2 } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { toast } from "sonner";

export default function CustomerProjectsSection({
  customerId,
  canMutate = true,
}: {
  customerId: number;
  canMutate?: boolean;
}) {
  const [, setLocation] = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const projectsQ = trpc.installProjects.listByCustomer.useQuery({ customerId });
  const createShare = trpc.installProjects.createShareLink.useMutation({
    onSuccess: (res) => {
      void navigator.clipboard.writeText(res.shareUrl);
      toast.success("Share link copied");
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base text-[#1a2744]">Projects</CardTitle>
          <p className="text-xs text-muted-foreground">
            Operational projects linked to this customer&apos;s proposals.
          </p>
        </div>
        {canMutate ? (
        <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          ADD PROJECT
        </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {projectsQ.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading projects…
          </div>
        ) : (projectsQ.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No projects yet for this customer.</p>
        ) : (
          <div className="space-y-2">
            {(projectsQ.data ?? []).map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#1a2744]/10 bg-white p-3"
              >
                <div>
                  <p className="font-medium text-[#1a2744]">{p.projectName}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.projectRef} · {p.proposalRef} · {p.status} · Updated{" "}
                    {format(new Date(p.updatedAt), "dd MMM yyyy")}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button type="button" size="sm" variant="outline" asChild>
                    <Link href={`/projects/${p.id}`}>
                      <FolderOpen className="mr-1 h-3.5 w-3.5" />
                      OPEN PROJECT
                    </Link>
                  </Button>
                  {canMutate ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={createShare.isPending}
                    onClick={() => createShare.mutate({ projectId: p.id })}
                  >
                    <Share2 className="mr-1 h-3.5 w-3.5" />
                    SHARE FOLDER
                  </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        customerId={customerId}
        onCreated={(projectId) => {
          void projectsQ.refetch();
          setLocation(`/projects/${projectId}`);
        }}
      />
    </Card>
  );
}
